import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsTouchUi } from '@/shared/lib/media';

import { Icons } from './icons';
import styles from './MultiSelect.module.css';
import { Popover } from './Popover';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** A colored dot (labels). */
  color?: string;
}

interface Props {
  /** Accessible name of the control and of its popup. */
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Shown next to the `+` while nothing is selected. */
  placeholder?: string;
  disabled?: boolean;
}

/**
 * A compact multi-select: the chosen items as removable chips and a small `+` that opens a
 * searchable checklist (the pattern of the task labels). Several items can be ticked in one go;
 * Escape / an outside press / Done closes the list.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  searchPlaceholder,
  emptyText,
  placeholder,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const touch = useIsTouchUi();
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const selected = value
    .map((v) => byValue.get(v))
    .filter((o): o is MultiSelectOption => o !== undefined);
  // Nothing chosen yet: the trigger says what it adds instead of being a bare `+`.
  const labelled = selected.length === 0 && !!placeholder;
  const needle = query.trim().toLocaleLowerCase();
  const shown = options.filter(
    (o) => needle === '' || o.label.toLocaleLowerCase().includes(needle),
  );

  return (
    <div className={styles.root}>
      <ul className={styles.chips} aria-label={label}>
        {selected.map((option) => (
          <li key={option.value} className={styles.chip}>
            {option.color && (
              <span aria-hidden className={styles.dot} style={{ background: option.color }} />
            )}
            <span className={styles.chipName}>{option.label}</span>
            {!disabled && (
              <button
                type="button"
                className={styles.remove}
                aria-label={t('common.removeItem', { name: option.label })}
                onClick={() => {
                  onChange(value.filter((v) => v !== option.value));
                }}
              >
                <Icons.close />
              </button>
            )}
          </li>
        ))}
        {!disabled && (
          <li>
            <Popover
              label={label}
              width={320}
              trigger={(props, open) => (
                <button
                  {...props}
                  type="button"
                  className={styles.add}
                  aria-label={label}
                  title={label}
                  aria-expanded={open}
                  data-labelled={labelled || undefined}
                >
                  <Icons.plus />
                  {labelled && <span>{placeholder}</span>}
                </button>
              )}
            >
              {(close) => (
                <div className={styles.panel}>
                  <input
                    type="search"
                    className={styles.search}
                    value={query}
                    autoFocus={!touch}
                    placeholder={searchPlaceholder ?? t('common.search')}
                    aria-label={searchPlaceholder ?? t('common.search')}
                    onChange={(event) => {
                      setQuery(event.target.value);
                    }}
                  />
                  <ul
                    className={styles.options}
                    role="listbox"
                    aria-multiselectable
                    aria-label={label}
                  >
                    {shown.map((option) => {
                      const on = value.includes(option.value);
                      return (
                        <li key={option.value} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={on}
                            className={on ? styles.optionOn : styles.option}
                            onClick={() => {
                              onChange(
                                on
                                  ? value.filter((v) => v !== option.value)
                                  : [...value, option.value],
                              );
                            }}
                          >
                            <span aria-hidden className={styles.check}>
                              {on ? '✓' : ''}
                            </span>
                            {option.color && (
                              <span
                                aria-hidden
                                className={styles.dot}
                                style={{ background: option.color }}
                              />
                            )}
                            <span className={styles.chipName}>{option.label}</span>
                          </button>
                        </li>
                      );
                    })}
                    {shown.length === 0 && (
                      <li className={styles.empty}>{emptyText ?? t('common.noResults')}</li>
                    )}
                  </ul>
                  <button type="button" className={styles.done} onClick={close}>
                    {t('common.done')}
                  </button>
                </div>
              )}
            </Popover>
          </li>
        )}
      </ul>
    </div>
  );
}
